'use strict';

/**
 * Remembers how many cameras / EvoWalks were connected on the Devices and
 * EvoWalk pages so the Record page can compare against live connections.
 */

export const DEVICE_CONNECTION_MEMORY_KEY = 'evovision:expectedDeviceConnections';

const EMPTY = Object.freeze({
  cameras: Object.freeze({ count: 0, names: Object.freeze([]) }),
  evowalks: Object.freeze({ count: 0, names: Object.freeze([]) }),
  hasCamerasMemory: false,
  hasEvowalksMemory: false,
});

/** GoPro / EvoWalk APIs return connected as 'yes'/'no' or boolean. */
export function isDeviceConnected(device) {
  if (!device || typeof device !== 'object') return false;
  return device.connected === 'yes' || device.connected === true;
}

function deviceLabel(device) {
  const name = typeof device.name === 'string' ? device.name.trim() : '';
  if (name) return name;
  return typeof device.advertised_name === 'string' ? device.advertised_name.trim() : '';
}

/** Address distinguishes units that share an advertised name. */
function evowalkMemoryKey(device) {
  const address = typeof device.address === 'string' ? device.address.trim() : '';
  if (address) return address;
  return deviceLabel(device);
}

function connectedKeys(devices, pick) {
  if (!Array.isArray(devices)) return [];
  return devices
    .filter(isDeviceConnected)
    .map((device) => pick(device).trim())
    .filter(Boolean);
}

export function connectedDeviceNames(devices) {
  return connectedKeys(devices, deviceLabel);
}

export function countConnectedDevices(devices) {
  return connectedDeviceNames(devices).length;
}

function normalizeNames(names) {
  if (!Array.isArray(names)) return [];
  return names
    .filter((name) => typeof name === 'string' && name.trim() !== '')
    .map((name) => name.trim());
}

function normalizeBucket(bucket) {
  if (!bucket || typeof bucket !== 'object') {
    return { count: 0, names: [] };
  }
  const names = normalizeNames(bucket.names);
  const count =
    typeof bucket.count === 'number' && Number.isFinite(bucket.count) && bucket.count >= 0
      ? Math.floor(bucket.count)
      : names.length;
  return { count, names };
}

export function loadExpectedConnections() {
  let raw = null;
  try {
    raw = localStorage.getItem(DEVICE_CONNECTION_MEMORY_KEY);
  } catch (err) {
    return {
      cameras: { count: 0, names: [] },
      evowalks: { count: 0, names: [] },
      hasCamerasMemory: false,
      hasEvowalksMemory: false,
    };
  }
  if (!raw) {
    return {
      cameras: { count: 0, names: [] },
      evowalks: { count: 0, names: [] },
      hasCamerasMemory: false,
      hasEvowalksMemory: false,
    };
  }
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return {
      cameras: { count: 0, names: [] },
      evowalks: { count: 0, names: [] },
      hasCamerasMemory: false,
      hasEvowalksMemory: false,
    };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {
      cameras: { count: 0, names: [] },
      evowalks: { count: 0, names: [] },
      hasCamerasMemory: false,
      hasEvowalksMemory: false,
    };
  }
  return {
    cameras: normalizeBucket(parsed.cameras),
    evowalks: normalizeBucket(parsed.evowalks),
    hasCamerasMemory: parsed.hasCamerasMemory === true,
    hasEvowalksMemory: parsed.hasEvowalksMemory === true,
  };
}

function persist(memory) {
  try {
    localStorage.setItem(DEVICE_CONNECTION_MEMORY_KEY, JSON.stringify(memory));
  } catch (err) {
    // Memory is advisory; a blocked store must not break Devices / Record.
  }
}

function withUpdatedBucket(existing, key, devices, flagKey, pick = deviceLabel) {
  const names = connectedKeys(devices, pick);
  const next = {
    cameras: { ...existing.cameras, names: [...existing.cameras.names] },
    evowalks: { ...existing.evowalks, names: [...existing.evowalks.names] },
    hasCamerasMemory: existing.hasCamerasMemory,
    hasEvowalksMemory: existing.hasEvowalksMemory,
  };
  next[key] = { count: names.length, names };
  next[flagKey] = true;
  persist(next);
  return next;
}

/** Call after the Cameras page refreshes its GoPro list. */
export function rememberConnectedCameras(devices) {
  return withUpdatedBucket(loadExpectedConnections(), 'cameras', devices, 'hasCamerasMemory');
}

/** Call after the EvoWalk page refreshes its device list. */
export function rememberConnectedEvowalks(devices) {
  return withUpdatedBucket(loadExpectedConnections(), 'evowalks', devices, 'hasEvowalksMemory', evowalkMemoryKey);
}

/**
 * Compare remembered counts to what is currently connected.
 * When a side has never been remembered, it is not treated as a mismatch.
 */
export function compareConnectionCounts(expected, currentCameras, currentEvowalks, evowalkEnabled) {
  const cameraCount = countConnectedDevices(currentCameras);
  const evowalkCount = countConnectedDevices(currentEvowalks);
  const camerasMismatch =
    expected.hasCamerasMemory === true && expected.cameras.count !== cameraCount;
  const evowalksMismatch =
    evowalkEnabled === true &&
    expected.hasEvowalksMemory === true &&
    expected.evowalks.count !== evowalkCount;

  return {
    cameraCount,
    evowalkCount,
    expectedCameraCount: expected.cameras.count,
    expectedEvowalkCount: expected.evowalks.count,
    camerasMismatch,
    evowalksMismatch,
    hasMismatch: camerasMismatch || evowalksMismatch,
  };
}

export function emptyConnectionMemory() {
  return {
    cameras: { count: EMPTY.cameras.count, names: [] },
    evowalks: { count: EMPTY.evowalks.count, names: [] },
    hasCamerasMemory: false,
    hasEvowalksMemory: false,
  };
}
